import React, { useCallback, useMemo, useState } from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { Box, Button, Slide, Typography } from '@mui/material';
import GridItem from '@jumbo/components/GridItem';
import GridContainer from '@jumbo/components/GridContainer';
import CmtCard from '@coremat/CmtCard';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import CmtCardHeader from '@coremat/CmtCard/CmtCardHeader';
import { useTheme } from '@emotion/react';
import SupportChatCard from './SupportChatCard';
import { BtnBold, BtnItalic, Editor, EditorProvider, Toolbar } from 'react-simple-wysiwyg';
import { useDeleteSupportChat, useDeleteSupportChatReply } from 'services/Data/SupportChatServices';
import { useTranslation } from 'react-i18next';
import DeleteModal from '@acnb/CustomComponents/_Modals/DeleteModal';
import SupportFileRow from './SupportFileRow';

const useStyles = makeStyles(theme => ({
  pageFull: {
    padding: theme.spacing(2),
    width: '100%',
    '& ul': {
      marginLeft: '2rem',
    },
    '& a': {
      color: theme.palette.grey[800],
      fontStyle: 'italic',
    },
  },
}));

const SupportChatSharedContainer = ({
  handleAddNewNote,
  isPostingSupportChat,
  supportChats,
  gettingSupportChats,
  fromProfile,
  disableSupportChat,
  openUploadFileModal,
  files,
  handleRemoveFile,
  handleResetFile,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const [supportChatToDelete, setSupportChatToDelete] = useState(null);
  const [supportChatReplyToDelete, setSupportChatReplyToDelete] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [showNewNote, setShowNewNote] = useState(false);

  const { mutate: deleteSupportChatReply, isPending: isDeletingSupportChatReply } = useDeleteSupportChatReply();
  const { mutate: deleteSupportChat, isPending: isDeletingSupportChat } = useDeleteSupportChat();

  const handleCloseDeleteModal = useCallback(() => {
    setSupportChatToDelete(null);
    setSupportChatReplyToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (supportChatToDelete) {
      deleteSupportChat(supportChatToDelete);
    } else if (supportChatReplyToDelete) {
      deleteSupportChatReply(supportChatReplyToDelete);
    }
  }, [deleteSupportChat, deleteSupportChatReply, supportChatReplyToDelete, supportChatToDelete]);

  const messageIsEmpty = useMemo(() => !newNote || newNote === '<br>', [newNote]);
  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Box className={classes.pageFull}>
        <DeleteModal
          handleCloseModal={handleCloseDeleteModal}
          item={supportChatToDelete || supportChatReplyToDelete}
          message={
            supportChatToDelete
              ? t('confirmDeleteSupportChatText', {
                  note: supportChatToDelete?.note,
                })
              : t('confirmDeleteSupportChatReplyText', {
                  note: supportChatReplyToDelete?.note,
                })
          }
          deleteAction={handleConfirmDelete}
          isDisabled={isDeletingSupportChat || isDeletingSupportChatReply}
        />

        <GridItem xs={12} md={fromProfile ? 12 : 6} style={{ textAlign: 'center' }}>
          {!disableSupportChat ? (
            showNewNote ? (
              <CmtCard sx={{ height: '100%', padding: 0 }} className={classes.itemCard}>
                <CmtCardHeader
                  separator={{
                    color: theme.palette.borderColor.dark,
                  }}
                  title={t('askQuestionTitle')}
                />
                <CmtCardContent className={classes.itemCardContent} sx={{ height: '100%', padding: 0 }}>
                  <Box>
                    <Typography>{t('askQuestionInfo')}</Typography>
                    <EditorProvider>
                      <Editor value={newNote} onChange={({ target: { value } }) => setNewNote(value)}>
                        <Toolbar>
                          <BtnBold />
                          <BtnItalic />
                        </Toolbar>
                      </Editor>
                    </EditorProvider>
                  </Box>

                  <Box sx={{ height: '4rem', display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                    {files?.map(f => (
                      <SupportFileRow file={f} handleRemoveFile={handleRemoveFile} />
                    ))}
                  </Box>
                  <Button
                    sx={{
                      my: 3,
                      color: '#000',
                      boxShadow: 'none',
                      '&:hover': { backgroundColor: '#EFBA29', boxShadow: 'none' },
                    }}
                    variant="contained"
                    color="secondary"
                    disabled={isPostingSupportChat || messageIsEmpty}
                    onClick={() => {
                      openUploadFileModal();
                    }}>
                    <Typography variant="h1" sx={{ fontSize: { xs: 16, sm: 18, md: 22 }, py: 2 }}>
                      {t('addFiles')}
                    </Typography>
                  </Button>

                  <Button
                    sx={{
                      my: 3,
                      ml: 2,
                      color: '#000',
                      boxShadow: 'none',
                      '&:hover': { backgroundColor: '#EFBA29', boxShadow: 'none' },
                    }}
                    variant="contained"
                    color="secondary"
                    disabled={isPostingSupportChat || messageIsEmpty}
                    onClick={() => {
                      handleAddNewNote(newNote);
                      setShowNewNote(false);
                      setNewNote('');
                    }}>
                    <Typography variant="h1" sx={{ fontSize: { xs: 16, sm: 18, md: 22 }, py: 2 }}>
                      {t('askQuestionSubmit')}
                    </Typography>
                  </Button>

                  <Button
                    sx={{ my: 3, ml: 2 }}
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setShowNewNote(false);
                      setNewNote('');
                    }}>
                    <Typography variant="h1" sx={{ fontSize: { xs: 16, sm: 18, md: 22 }, py: 2 }}>
                      {t('cancel')}
                    </Typography>
                  </Button>
                </CmtCardContent>
              </CmtCard>
            ) : (
              <Button
                sx={{
                  my: 3,
                  color: '#000',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#EFBA29',
                    boxShadow: 'none',
                  },
                }}
                variant="contained"
                color="secondary"
                onClick={() => {
                  setShowNewNote(true);
                }}>
                <Typography variant="h1" sx={{ fontSize: { xs: 16, sm: 18, md: 22 }, py: 2 }}>
                  {t('askQuestionAdd')}
                </Typography>
              </Button>
            )
          ) : null}
        </GridItem>
        <GridContainer sx={{ mt: 1 }} justifyContent="center">
          {!showNewNote &&
            supportChats?.map(supportChat => (
              <SupportChatCard
                fromProfile={fromProfile}
                supportChat={supportChat}
                handleDeleteSupportChat={supportChat => setSupportChatToDelete(supportChat)}
                handleDeleteSupportChatReply={supportChatReply => setSupportChatReplyToDelete(supportChatReply)}
                disableSupportChat={disableSupportChat}
                openUploadFileModal={openUploadFileModal}
                handleRemoveFile={handleRemoveFile}
                files={files}
                handleResetFile={handleResetFile}
              />
            ))}

          {!showNewNote &&
            [1, 2].map(l => (
              <GridItem key={l} xs={12} style={{ minHeight: '3rem' }}>
                <SkeletonWrapper
                  skeletonProps={{
                    height: 400,
                  }}
                  isLoading={gettingSupportChats}
                />
              </GridItem>
            ))}
        </GridContainer>
      </Box>
    </Slide>
  );
};

export default SupportChatSharedContainer;
